import { IServiceVideoChatDevice } from "@obsbot/remo-services";
import React, { ReactElement, useState, useEffect, useCallback } from "react";
import { SettingComponentsType } from "..";
import {
    useSafePromise,
    SettingSelector,
    SettingSlider,
    SettingMicLevel,
    SettingSwitch,
} from "remo-components";

const AudioSettingPage = ({ t, rtc, preference }: SettingComponentsType): ReactElement => {
    const sp = useSafePromise();
    // 麦克风设备
    const [micDevices, setMicDevices] = useState<IServiceVideoChatDevice[]>([]);
    // 当前的麦克风设备ID
    const [currentMicDeviceID, setCurrentMicDeviceID] = useState<string | null>(null);
    // 扬声器设备
    const [speakerDevices, setSpeakerDevices] = useState<IServiceVideoChatDevice[]>([]);
    // 当前的扬声器设备ID
    const [currentSpeakerDeviceID, setCurrentSpeakerDeviceID] = useState<string | null>(null);
    // 当前的麦克风音量
    const [currentMicVolume, setCurrentMicVolume] = useState<number>(rtc.getMicVolume());
    // 当前的扬声器音量
    const [currentSpeakerVolume, setCurrentSpeakerVolume] = useState<number>(
        rtc.getSpeakerVolume(),
    );
    // 麦克风测试的音量
    const [micTestVolume, setMicTestVolume] = useState<number>(0);
    // 是否正在测试麦克风
    const [isTestingMic, setIsTestingMic] = useState<boolean>(false);
    // 是否开启回声抵消
    const [isEnableAEC, setIsEnableAEC] = useState<boolean>(
        preference?.enabledEchoCancellation ?? false,
    );
    // 麦克风降噪强度
    const [micNoiseSuppression, setMicNoiseSuppression] = useState<number>(
        preference?.noiseSuppressionLevel ?? 0,
    );
    // 是否开启麦克风自动增益
    const [isEnableGain, setIsEnableGain] = useState<boolean>(preference?.autoGainControl ?? false);
    // 监听麦克风设备变化
    useEffect(() => {
        const updateMicDevices = async (deviceID?: string): Promise<void> => {
            const devices = await sp(rtc.getMicDevices());
            setMicDevices(devices);
            if (devices.length === 0) {
                setCurrentMicDeviceID(null);
                return;
            }
            const micId = deviceID || rtc.getMicID() || devices[0]?.deviceId;
            setCurrentMicDeviceID(micId || null);
            if (devices.every(device => device.deviceId !== micId)) {
                setCurrentMicDeviceID(null);
            }
            if (micId) {
                onMicChanged(micId);
            }
        };
        updateMicDevices();
        rtc.events.on("mic-changed", updateMicDevices);
        return () => {
            rtc.events.off("mic-changed", updateMicDevices);
        };
    }, [rtc, sp]);
    // 监听选择麦克风设备
    const onMicChanged = useCallback(
        (deviceID: string): void => {
            rtc.setMicID(deviceID);
        },
        [rtc],
    );
    // 监听扬声器设备变化
    useEffect(() => {
        const updateSpeakerDevices = async (deviceID?: string): Promise<void> => {
            const devices = await sp(rtc.getSpeakerDevices());
            setSpeakerDevices(devices);
            if (devices.length === 0) {
                setCurrentSpeakerDeviceID(null);
                return;
            }
            const speakerId = deviceID || rtc.getSpeakerID() || devices[0]?.deviceId;
            setCurrentSpeakerDeviceID(speakerId || null);
            if (speakerId) {
                onSpeakerChanged(speakerId);
            }
        };
        updateSpeakerDevices();
        rtc.events.on("speaker-changed", updateSpeakerDevices);
        return () => {
            rtc.events.off("speaker-changed", updateSpeakerDevices);
        };
    }, [rtc, sp]);
    // 监听选择扬声器设备
    const onSpeakerChanged = useCallback(
        (deviceID: string): void => {
            rtc.setSpeakerID(deviceID);
        },
        [rtc],
    );
    const onSetMicVolume = useCallback(
        (volume: number): void => {
            rtc.setCaptureVolume(volume);
            setCurrentMicVolume(volume);
        },
        [rtc],
    );
    const onSetSpeakerVolume = useCallback(
        (volume: number): void => {
            rtc.setPlayoutVolume(volume);
            setCurrentSpeakerVolume(volume);
        },
        [rtc],
    );
    // 监听麦克风测试的音量变化
    useEffect(() => {
        const handleUpdateMicVolume = (volume: number): void => {
            setMicTestVolume(volume);
        };
        rtc.events.on("test-mic-volume-changed", handleUpdateMicVolume);
        return () => {
            rtc.events.off("test-mic-volume-changed", handleUpdateMicVolume);
        };
    }, [rtc]);
    // 开始测试麦克风
    const onStartMicTest = useCallback(async (): Promise<void> => {
        if (currentMicDeviceID && !isTestingMic) {
            rtc.startMicTest();
            setIsTestingMic(true);
        } else {
            rtc.stopMicTest();
            setMicTestVolume(0);
            setIsTestingMic(false);
        }
    }, [rtc, currentMicDeviceID, isTestingMic]);
    useEffect(() => {
        return rtc.stopMicTest();
    }, [rtc]);
    // TODO: 开启回声抵消/麦克风降噪/麦克风自动增益
    const handleEnableAESChange = (enabled: boolean): void => {
        setIsEnableAEC(enabled);
        preference?.updateEnabledEchoCancellation(enabled);
        rtc.setAudioAEC(enabled);
    };
    const handleEnableGainChange = (enabled: boolean): void => {
        setIsEnableGain(enabled);
        preference?.updateAutoGainControl(enabled);
        rtc.setAudioAGC(enabled);
    };
    const handleNoiseSuppressionChange = (level: number): void => {
        setMicNoiseSuppression(level);
        preference?.updateNoiseSuppressionLevel(level);
    };
    return (
        <div className="rtc-setting-page rtc-setting-audio">
            <div className="rtc-setting-page-device">
                <div className="rtc-setting-page-title">
                    {t("settings.rtc-setting.device&volume")}
                </div>
                <div
                    className="rtc-setting-page-content rtc-setting-device-content"
                    style={{ height: 334 }}
                >
                    <div className="rtc-setting-show-item">
                        <div className="rtc-setting-page-label">
                            {t("settings.rtc-setting.inputDevice")}
                        </div>
                        <div className="rtc-setting-page-input wide">
                            <SettingSelector
                                key={currentMicDeviceID}
                                className="rtc-setting-selector"
                                defaultValue={currentMicDeviceID}
                                disabled={micDevices.length === 0}
                                options={micDevices.map(device => ({
                                    label: device.label,
                                    value: device.deviceId,
                                    title: "",
                                }))}
                                onChange={onMicChanged}
                            />
                            {micDevices.length === 0 && (
                                <div className="rtc-setting-item-no-devices">
                                    {t("settings.rtc-setting.noMic")}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="rtc-setting-show-item">
                        <div className="rtc-setting-page-label">
                            {t("settings.rtc-setting.outputDevice")}
                        </div>
                        <div className="rtc-setting-page-input wide">
                            <SettingSelector
                                key={currentSpeakerDeviceID}
                                className="rtc-setting-selector"
                                defaultValue={currentSpeakerDeviceID}
                                disabled={speakerDevices.length === 0}
                                options={speakerDevices.map(device => ({
                                    label: device.label,
                                    value: device.deviceId,
                                    title: "",
                                }))}
                                onChange={onSpeakerChanged}
                            />
                            {speakerDevices.length === 0 && (
                                <div className="rtc-setting-item-no-devices">
                                    {t("settings.rtc-setting.noSpeaker")}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="rtc-setting-show-item">
                        <div className="rtc-setting-page-label">
                            {t("settings.rtc-setting.micInputVolume")}
                        </div>
                        <div className="rtc-setting-page-input wide rtc-setting-page-slider">
                            <SettingSlider
                                bg_handle="#5333D5"
                                bg_track="linear-gradient(90deg, #744CF6 0%, #5333D5 100%)"
                                className="rtc-setting-page-slider"
                                value={currentMicVolume}
                                onChange={onSetMicVolume}
                            />
                        </div>
                    </div>
                    <div className="rtc-setting-show-item">
                        <div className="rtc-setting-page-label">
                            {t("settings.rtc-setting.voiceOutputVolume")}
                        </div>
                        <div className="rtc-setting-page-input wide rtc-setting-page-slider">
                            <SettingSlider
                                bg_handle="#5333D5"
                                bg_track="linear-gradient(90deg, #744CF6 0%, #5333D5 100%)"
                                className="rtc-setting-page-slider"
                                value={currentSpeakerVolume}
                                onChange={onSetSpeakerVolume}
                            />
                        </div>
                    </div>
                    <div className="rtc-setting-show-item rtc-setting-mic-test">
                        <div className="rtc-setting-mic-check">
                            <div className="rtc-setting-page-label rtc-setting-mic-test-title">
                                {t("settings.rtc-setting.micTest")}
                            </div>
                            <SettingMicLevel
                                className="rtc-setting-mic-level"
                                micLevel={micTestVolume}
                                width="99%"
                            />
                            <div className="rtc-setting-mic-check-btn" onClick={onStartMicTest}>
                                {isTestingMic
                                    ? t("settings.rtc-setting.stopCheck")
                                    : t("settings.rtc-setting.check")}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="rtc-setting-page-voice-advance">
                <div className="rtc-setting-page-title" style={{ marginTop: 20 }}>
                    {t("settings.rtc-setting.voiceBetter")}
                </div>
                <div className="rtc-setting-page-content rtc-setting-voice-content">
                    <div className="rtc-setting-show-item">
                        <div className="rtc-setting-page-label">
                            {t("settings.rtc-setting.echoCancellation")}
                        </div>
                        <div className="rtc-setting-page-input">
                            <SettingSwitch checked={isEnableAEC} onChange={handleEnableAESChange} />
                        </div>
                    </div>
                    <div className="rtc-setting-show-item">
                        <div className="rtc-setting-page-label">
                            {t("settings.rtc-setting.noiseSuppression")}
                        </div>
                        <div className="rtc-setting-page-input">
                            <SettingSelector
                                defaultValue={micNoiseSuppression}
                                options={[
                                    {
                                        value: -1,
                                        label: t("settings.rtc-setting.close"),
                                        title: "",
                                    },
                                    { value: 0, label: t("settings.rtc-setting.weak"), title: "" },
                                    {
                                        value: 1,
                                        label: t("settings.rtc-setting.middle"),
                                        title: "",
                                    },
                                    {
                                        value: 2,
                                        label: t("settings.rtc-setting.strong"),
                                        title: "",
                                    },
                                ]}
                                placement="topLeft"
                                onChange={handleNoiseSuppressionChange}
                            />
                        </div>
                    </div>
                    <div className="rtc-setting-show-item">
                        <div className="rtc-setting-page-label">
                            {t("settings.rtc-setting.autoGainControl")}
                        </div>
                        <div className="rtc-setting-page-input">
                            <SettingSwitch
                                checked={isEnableGain}
                                onChange={handleEnableGainChange}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
export default AudioSettingPage;
